import { History } from 'history';

import {
  ActionType,
  ErrorTypes,
  LoginTypes,
  ResetPasswordTypes,
  CreateNewPasswordType,
  SignUpTypes,
  UserInfosTypes,
  ICustomMenuItem,
} from 'interfaces';
import {
  action,
  createRequestTypes,
  FAILURE,
  REQUEST,
  RequestTypes,
  SUCCESS,
} from 'services/api';

export const LOGIN: RequestTypes = createRequestTypes('LOGIN');
export const SIGNUP: RequestTypes = createRequestTypes('SIGNUP');
export const FORGOT_PASSWORD: RequestTypes = createRequestTypes(
  'FORGOT_PASSWORD',
);
export const RESET_PASSWORD: RequestTypes = createRequestTypes(
  'RESET_PASSWORD',
);
export const CREATE_NEW_PASSWORD: RequestTypes = createRequestTypes(
  'CREATE_NEW_PASSWORD',
);
export const LOGOUT = 'LOGOUT';
export const CHECK_AUTH: RequestTypes = createRequestTypes('CHECK_AUTH');
export const CHECK_SSO_AZURE_CONFIG: RequestTypes = createRequestTypes(
  'CHECK_SSO_AZURE_CONFIG',
);
export const CHECK_SSO_JWT_CONFIG: RequestTypes = createRequestTypes(
  'CHECK_SSO_JWT_CONFIG',
);
export const CHECK_SSO_SAML_CONFIG: RequestTypes = createRequestTypes(
  'CHECK_SSO_SAML_CONFIG',
);
export const CHECK_PASSWORD_RESET_TOKEN: RequestTypes = createRequestTypes(
  'CHECK_PASSWORD_RESET_TOKEN',
);
export const SET_CHECK_PASSWORD_RESET_TOKEN_LOADING =
  'SET_CHECK_PASSWORD_RESET_TOKEN_LOADING';
export const SET_APP_LOADING = 'SET_APP_LOADING';
export const SET_AUTH_LOADING = 'SET_AUTH_LOADING';
export const SET_LOGIN_LOADING = 'SET_LOGIN_LOADING';
export const APP_CONFIG: RequestTypes = createRequestTypes('APP_CONFIG');

export const APP_OPTIONS: RequestTypes = createRequestTypes('APP_OPTIONS');
export const SET_OPTIONS_LOADING = 'SET_OPTIONS_LOADING';

export const GET_CUSTOM_MENU_ITEMS_ACTION: RequestTypes = createRequestTypes(
  'GET_CUSTOM_MENU_ITEMS_ACTION',
);
export const SET_MENU_LOADING = 'SET_MENU_LOADING';

export const USER_POINTS: RequestTypes = createRequestTypes('USER_POINTS');
export const SET_POINTS_LOADING = 'SET_POINTS_LOADING';

export const UPDATE_USER_CONDITIONS_AND_TERMS: RequestTypes = createRequestTypes(
  'UPDATE_USER_CONDITIONS_AND_TERMS',
);

export const FEATURE_BY_NAME: RequestTypes = createRequestTypes(
  'FEATURE_BY_NAME',
);
export const SET_FEATURE_LOADING = 'SET_FEATURE_LOADING';

export const PRODUCT_CURRENCY: RequestTypes = createRequestTypes(
  'PRODUCT_CURRENCY',
);

export const setAppLoading = (isLoading: boolean): ActionType =>
  action(SET_APP_LOADING, { isLoading });

export const setAuthLoading = (isLoading: boolean): ActionType =>
  action(SET_AUTH_LOADING, { isLoading });

export const setLoginLoading = (isLoading: boolean): ActionType =>
  action(SET_LOGIN_LOADING, { isLoading });

export const setLogout = (): ActionType => action(LOGOUT);

export const login = {
  request: (
    { username, password }: LoginTypes,
    callback: (token: string) => void,
  ): ActionType =>
    action(LOGIN[REQUEST], {
      username,
      password,
      callback,
    }),
  success: (token: string): ActionType => action(LOGIN[SUCCESS], { token }),
  failure: (error: ErrorTypes): ActionType => action(LOGIN[FAILURE], error),
};

export const signup = {
  request: (data: SignUpTypes, onSignUp: (token: string) => void): ActionType =>
    action(SIGNUP[REQUEST], { data, onSignUp }),
  success: (): ActionType => action(SIGNUP[SUCCESS]),
  failure: (error: ErrorTypes): ActionType => action(SIGNUP[FAILURE], error),
};

export const forgotPassword = {
  request: (email: string): ActionType =>
    action(FORGOT_PASSWORD[REQUEST], { email }),
  success: (): ActionType => action(FORGOT_PASSWORD[SUCCESS]),
  failure: (error: ErrorTypes): ActionType =>
    action(FORGOT_PASSWORD[FAILURE], error),
};

export const resetPassword = {
  request: (values: ResetPasswordTypes, history: History): ActionType =>
    action(RESET_PASSWORD[REQUEST], {
      ...values,
      history,
    }),
  failure: (error: ErrorTypes): ActionType =>
    action(RESET_PASSWORD[FAILURE], error),
};

export const createNewPassword = {
  request: (
    data: CreateNewPasswordType,
    onCreate: (token: string) => void,
  ): ActionType =>
    action(CREATE_NEW_PASSWORD[REQUEST], {
      ...data,
      onCreate,
    }),
  success: (): ActionType => action(CREATE_NEW_PASSWORD[SUCCESS]),
  failure: (error: ErrorTypes): ActionType =>
    action(CREATE_NEW_PASSWORD[FAILURE], error),
};

export const checkAuth = {
  request: (token: string): ActionType =>
    action(CHECK_AUTH[REQUEST], { token }),
  success: (token: string, userInfos: UserInfosTypes): ActionType =>
    action(CHECK_AUTH[SUCCESS], { token, userInfos }),
  failure: (error: ErrorTypes): ActionType =>
    action(CHECK_AUTH[FAILURE], error),
};

export const checkSsoAzureConfig = {
  request: (): ActionType => action(CHECK_SSO_AZURE_CONFIG[REQUEST]),
  success: (hasSsoAzureConfigured: boolean): ActionType =>
    action(CHECK_SSO_AZURE_CONFIG[SUCCESS], { hasSsoAzureConfigured }),
  failure: (error: ErrorTypes): ActionType =>
    action(CHECK_SSO_AZURE_CONFIG[FAILURE], error),
};

export const checkSsoJwtConfig = {
  request: (): ActionType => action(CHECK_SSO_JWT_CONFIG[REQUEST]),
  success: (hasSsoJwtConfigured: boolean): ActionType =>
    action(CHECK_SSO_JWT_CONFIG[SUCCESS], { hasSsoJwtConfigured }),
  failure: (error: ErrorTypes): ActionType =>
    action(CHECK_SSO_JWT_CONFIG[FAILURE], error),
};

export const checkSsoSamlConfig = {
  request: (): ActionType => action(CHECK_SSO_SAML_CONFIG[REQUEST]),
  success: (hasSsoSamlConfigured: boolean): ActionType =>
    action(CHECK_SSO_SAML_CONFIG[SUCCESS], { hasSsoSamlConfigured }),
  failure: (error: ErrorTypes): ActionType =>
    action(CHECK_SSO_SAML_CONFIG[FAILURE], error),
};

export const checkPasswordResetToken = {
  request: (token: string): ActionType =>
    action(CHECK_PASSWORD_RESET_TOKEN[REQUEST], { token }),
  success: ({
    isValid,
    email,
  }: {
    isValid: boolean;
    email?: string;
  }): ActionType =>
    action(CHECK_PASSWORD_RESET_TOKEN[SUCCESS], { isValid, email }),
  failure: (error: ErrorTypes): ActionType =>
    action(CHECK_PASSWORD_RESET_TOKEN[FAILURE], error),
};

export const setCheckPasswordResetTokenLoading = (
  isLoading: boolean,
): ActionType => action(SET_CHECK_PASSWORD_RESET_TOKEN_LOADING, { isLoading });

export const appConfig = {
  request: (): ActionType => action(APP_CONFIG[REQUEST]),
  success: (data: unknown): ActionType => action(APP_CONFIG[SUCCESS], { data }),
  failure: (error: ErrorTypes): ActionType =>
    action(APP_CONFIG[FAILURE], error),
};

export const appOptions = {
  request: (): ActionType => action(APP_OPTIONS[REQUEST]),
  success: (data: string): ActionType => action(APP_OPTIONS[SUCCESS], { data }),
  failure: (error: ErrorTypes): ActionType =>
    action(APP_OPTIONS[FAILURE], error),
};

export const productCurrency = {
  request: (): ActionType => action(PRODUCT_CURRENCY[REQUEST]),
  success: (data: string): ActionType =>
    action(PRODUCT_CURRENCY[SUCCESS], { data }),
  failure: (error: ErrorTypes): ActionType =>
    action(PRODUCT_CURRENCY[FAILURE], error),
};

export const setOptionsLoading = (isLoading: boolean): ActionType =>
  action(SET_OPTIONS_LOADING, { isLoading });

export const getCustomMenuItemsAction = {
  request: (): ActionType => action(GET_CUSTOM_MENU_ITEMS_ACTION[REQUEST]),
  success: (menuItems: ICustomMenuItem[]): ActionType =>
    action(GET_CUSTOM_MENU_ITEMS_ACTION[SUCCESS], { menuItems }),
  failure: (error: ErrorTypes): ActionType =>
    action(GET_CUSTOM_MENU_ITEMS_ACTION[FAILURE], error),
};

export const setMenuLoading = (isLoading: boolean): ActionType =>
  action(SET_MENU_LOADING, { isLoading });

export const userPoints = {
  request: (id: string): ActionType => action(USER_POINTS[REQUEST], { id }),
  success: (points: number): ActionType =>
    action(USER_POINTS[SUCCESS], { points }),
  failure: (error: ErrorTypes): ActionType =>
    action(USER_POINTS[FAILURE], error),
};

export const setPointsLoading = (isLoading: boolean): ActionType =>
  action(SET_POINTS_LOADING, { isLoading });

export const updateUserConditionsAndTerms = {
  request: (id: string): ActionType =>
    action(UPDATE_USER_CONDITIONS_AND_TERMS[REQUEST], { id }),
  success: (user: any): ActionType =>
    action(UPDATE_USER_CONDITIONS_AND_TERMS[SUCCESS], { user }),
  failure: (error: ErrorTypes): ActionType =>
    action(UPDATE_USER_CONDITIONS_AND_TERMS[FAILURE], error),
};

export const featureByName = {
  request: (name: string): ActionType =>
    action(FEATURE_BY_NAME[REQUEST], { name }),
  success: (name: string, data: unknown): ActionType =>
    action(FEATURE_BY_NAME[SUCCESS], { name, data }),
  failure: (name: string, error: ErrorTypes): ActionType =>
    action(FEATURE_BY_NAME[FAILURE], { name, error }),
};

export const setFeatureLoading = (
  name: string,
  isLoading: boolean,
): ActionType => action(SET_FEATURE_LOADING, { name, isLoading });
